<template>
  <Badge
    v-if="visible"
    class="ml-0"
    :class="{ 'spb-status-badge--compact': compactBadge }"
    :style="badgeStyle"
    data-cy="spb-status-badge"
    :text="text"
    :icon="icon"
    :color="color"
    :tooltip-text="tooltipText"
  >
    <template #prefix-icon>
      <span
        class="progress-billing-indicator progress-billing-badge"
        :class="{ 'progress-billing-indicator--compact': compactBadge }"
        aria-label="Progress Billing"
        data-cy="progress-billing-indicator"
      >
        PB
      </span>
    </template>
  </Badge>
</template>

<script setup lang="ts">
import { computed } from "vue";

import Badge from "@/components/Badge.vue";
import type { Invoice } from "@/typings/invoice";

const props = defineProps<{
  invoice: Invoice;
}>();

interface BadgeState {
  text: string;
  icon: string;
  color: "green" | "yellow" | "red" | "gray";
}

// State resolution mirrors the priorities used by .spb-row in
// Job/Tabs/InvoiceTab.vue: cancelled wins over needs-update, locked
// (finalized invoice) wins over healthy.
const state = computed<BadgeState | null>(() => {
  const plan = props.invoice?.progress_billing as
    | (Invoice["progress_billing"] & { cancelled?: boolean })
    | undefined;
  if (!plan?.is_progress_billing) return null;
  if (plan.cancelled) {
    return { text: "Plan Cancelled", icon: "fa-ban", color: "red" };
  }
  if (plan.plan_needs_review || plan.plan_drift) {
    return {
      text: "Plan Needs Review",
      icon: "fa-triangle-exclamation",
      color: "yellow"
    };
  }
  if (props.invoice.finalized) {
    return { text: "Plan Locked", icon: "fa-lock", color: "gray" };
  }
  return { text: "Configured", icon: "fa-circle-check", color: "green" };
});

const visible = computed(() => state.value !== null);
const text = computed(() => state.value?.text ?? "");
const icon = computed(() => state.value?.icon ?? "");
const color = computed(() => state.value?.color ?? "gray");
const compactBadge = computed(
  () => text.value === "Configured" || text.value === "Plan Locked"
);
const badgeStyle = computed(() =>
  compactBadge.value ? { paddingLeft: "2px" } : undefined
);

const tooltipText = computed(() => {
  const plan = props.invoice?.progress_billing;
  if (!plan?.is_progress_billing) return undefined;
  if (state.value?.text === "Plan Needs Review") {
    return "Plan Needs Review - See Below";
  }
  const drawCount = plan.installments?.length ?? 0;
  const currentLabel = plan.current_draw?.label;
  const parts = [
    drawCount ? `${drawCount} draws` : "",
    currentLabel ? `Current: ${currentLabel}` : ""
  ].filter(Boolean);
  return `Progress Billing:\n${parts.join(" · ") || state.value?.text}`;
});
</script>

<style lang="scss" scoped>
.spb-status-badge--compact {
  // Hook for per-state styling; the template already toggles this
  // class for the "Configured" and "Plan Locked" states.
}

.progress-billing-indicator {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  margin-right: 8px;
  min-width: 20px;
  height: 18px;
  padding: 0 3px;
  border-radius: 3px;
  background-color: #005eb8;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  line-height: 1;
}

.progress-billing-indicator--compact {
  margin-right: 8px;
}
</style>
